import { useEffect } from "react"
import {
  QueryHookOptions,
  useQuery as apolloUseQuery,
  OperationVariables,
  QueryResult,
  ApolloError,
  DocumentNode,
  ServerError,
  TypedDocumentNode,
} from "@apollo/client"
import { useAPIError } from "../APIError"
import { config } from "../config"
import { captureMessage, setContext, setTag, withScope } from "@sentry/browser"
import type { OperationDefinitionNode } from "graphql"
import { getApolloErrorExplanation } from "./getApolloErrorExplanation"

type ErrorCodes = "401" | "403" | "404" | "429" | "500"

function sendErrorToSentry(
  query: DocumentNode,
  error: ApolloError,
  ignoredErrorCodes?: ErrorCodes[],
) {
  const errorMessage = error.message.replace("\n", " | ")

  if (ignoredErrorCodes?.some(errorCode => errorMessage.includes(errorCode))) return

  const definition = query.definitions.find(
    definition => definition.kind === "OperationDefinition",
  ) as OperationDefinitionNode

  const definitionName = definition.name?.value

  withScope(scope => {
    scope.setLevel("error")

    const serverError = error.networkError as ServerError

    const errors: Record<string, string> = {
      graphQLErrors: JSON.stringify(error.graphQLErrors, null, 2),
      errorMessage,
    }

    if (serverError) {
      errors.apiErrorMessage =
        typeof serverError.result === "string"
          ? serverError.result
          : serverError.result?.error?.message
    }

    setContext("errors", errors)
    setTag("errorMessage", errors.errorMessage)

    setContext("query", {
      body: query.loc?.source.body,
    })

    captureMessage(`Error on query ${definitionName}`, "error")
  })
}

export type UseQueryOptions<
  T,
  O extends OperationVariables = OperationVariables,
> = QueryHookOptions<T, O> & {
  showErrorModal?: boolean
  ignoredErrorCodes?: ErrorCodes[]
}

export function useQuery<TData = any, TVariables extends OperationVariables = OperationVariables>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options?: UseQueryOptions<TData, TVariables>,
): QueryResult<TData, TVariables> {
  const { addError } = useAPIError()
  const showErrorModal = Object.hasOwnProperty.call(options ?? {}, "showErrorModal")
    ? options?.showErrorModal
    : true

  const { error, ...rest } = apolloUseQuery<TData, TVariables>(query, options)

  useEffect(() => {
    if (!error) {
      return
    }

    sendErrorToSentry(query, error, options?.ignoredErrorCodes)

    console.log(error.message, error.clientErrors, error.protocolErrors, error.graphQLErrors)

    if (showErrorModal) {
      addError(
        config.environmentIsProductionLike ? getApolloErrorExplanation(error) : error.toString(),
      )
    }
  }, [query, addError, error, showErrorModal, options?.ignoredErrorCodes])
  return { error, ...rest }
}
